import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const overHero = location.pathname === "/" && !scrolled;
  const onLight = !overHero;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-border/60" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="shrink-0 flex items-center">
          <img
            src="/schonell-logo.png"
            alt="Schonell Projects"
            className={`h-8 sm:h-9 w-auto ${onLight ? "" : "[filter:brightness(0)_invert(1)]"}`}
          />
        </Link>

        <ul className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}-link`}
                className={({ isActive }) => {
                  const base = "text-xs uppercase tracking-[0.18em] pb-1 transition-colors duration-300 border-b";
                  if (onLight) {
                    return `${base} ${isActive ? "text-ink border-ink" : "text-ink/60 hover:text-ink border-transparent"}`;
                  }
                  return `${base} ${isActive ? "text-cream border-cream" : "text-cream/70 hover:text-cream border-transparent"}`;
                }}
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <Link
          to="/contact"
          data-testid="nav-enquire-cta"
          className={`hidden md:inline-block px-6 py-3 text-xs uppercase tracking-[0.18em] transition-colors ${
            onLight
              ? "bg-ink text-cream hover:bg-ink/85"
              : "bg-cream text-ink hover:bg-cream/90"
          }`}
        >
          Enquire
        </Link>

        <button
          data-testid="mobile-menu-toggle"
          className={`md:hidden ${onLight ? "text-ink" : "text-cream"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-cream border-t border-border px-6 py-8">
          <ul className="flex flex-col gap-6">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
                  className="text-sm uppercase tracking-[0.2em] text-ink"
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
            <li>
              <Link
                to="/contact"
                data-testid="mobile-nav-enquire-cta"
                className="inline-block mt-2 bg-ink text-cream px-6 py-3 text-xs uppercase tracking-[0.18em]"
              >
                Enquire
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};
